/**
 * BulkCreateLinkModal
 *
 * Modal for creating relationships from one source endpoint to multiple targets.
 * Targets can be the currently selected endpoints.
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  BulkCreateRelationshipRequest,
  BulkCreateResponse,
  CaseEndpoint,
  QuestionTemplate,
} from '../../types/case-container'
import {
  BidirectionalToggle,
  ConnectionTypeSelect,
  ErrorBanner,
  FormActions,
  LabelInput,
  LargeBatchWarning,
  ModalHeader,
  NotesField,
  OpenQuestionSection,
  SourceTargetsPreview,
  StatusButtons,
  SuccessBanner,
} from './bulk-create-link-modal/bulk-create-link-modal.parts'
import { LARGE_BATCH_THRESHOLD } from './bulk-create-link-modal/bulk-create-link-modal.constants'
import { useBulkCreateLinkModal } from './bulk-create-link-modal/use-bulk-create-link-modal'

interface BulkCreateLinkModalProps {
  sourceEndpoint: CaseEndpoint
  targetIds: string[]
  endpoints: CaseEndpoint[]
  templates: QuestionTemplate[]
  onClose: () => void
  onCreate: (data: BulkCreateRelationshipRequest) => Promise<BulkCreateResponse | null>
  creating: boolean
  error: string | null
}

export function BulkCreateLinkModal(props: BulkCreateLinkModalProps): ReactElement {
  const { sourceEndpoint, targetIds, endpoints, templates, onClose, onCreate, creating, error } =
    props
  const { t } = useTranslation('cases')
  const state = useBulkCreateLinkModal({ sourceEndpoint, targetIds, endpoints, onCreate })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <ModalHeader totalRelationships={state.totalRelationships} onClose={onClose} t={t} />
        {state.result ? (
          <SuccessBanner result={state.result} onClose={onClose} t={t} />
        ) : (
          <>
            <SourceTargetsPreview
              sourceEndpoint={sourceEndpoint}
              targetEndpoints={state.targetEndpoints}
              t={t}
            />
            {state.totalRelationships > LARGE_BATCH_THRESHOLD && (
              <LargeBatchWarning totalRelationships={state.totalRelationships} t={t} />
            )}
            <form
              onSubmit={(e) => {
                void state.handleSubmit(e)
              }}
              className="space-y-4"
            >
              {error && <ErrorBanner error={error} />}
              <ConnectionTypeSelect
                connectionType={state.connectionType}
                setConnectionType={state.setConnectionType}
                t={t}
              />
              <StatusButtons status={state.status} setStatus={state.setStatus} t={t} />
              <LabelInput label={state.label} setLabel={state.setLabel} t={t} />
              <BidirectionalToggle
                bidirectional={state.bidirectional}
                setBidirectional={state.setBidirectional}
                t={t}
              />
              {state.isOpenQuestion ? (
                <OpenQuestionSection
                  templates={templates}
                  investigationNotes={state.investigationNotes}
                  setInvestigationNotes={state.setInvestigationNotes}
                  t={t}
                />
              ) : (
                <NotesField
                  investigationNotes={state.investigationNotes}
                  setInvestigationNotes={state.setInvestigationNotes}
                  t={t}
                />
              )}
              <FormActions
                onClose={onClose}
                creating={creating}
                validTargetCount={state.validTargetIds.length}
                totalRelationships={state.totalRelationships}
                t={t}
              />
            </form>
          </>
        )}
      </div>
    </div>
  )
}
